export const PROJECT_THUMBNAIL_FILE_LIMIT_BYTES = 2 * 1024 * 1024;
export const PROJECT_SAVE_REQUEST_LIMIT_BYTES = 8 * 1024 * 1024;

export interface ProjectSavePayloadLike {
  title: string;
  description?: string;
  workspaceXml: string;
  version?: string;
  thumbnail?: string;
  extensions?: Record<string, unknown>;
  isPublic?: boolean;
}

export interface ProjectSavePayloadSizeBreakdown {
  limitBytes: number;
  totalBytes: number;
  titleBytes: number;
  descriptionBytes: number;
  workspaceXmlBytes: number;
  thumbnailBytes: number;
  extensionsBytes: number;
}

const textEncoder = new TextEncoder();

const measureTextBytes = (value: string): number => textEncoder.encode(value).length;

export function formatByteSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = -1;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const precision = value >= 10 || unitIndex <= 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[Math.max(unitIndex, 0)]}`;
}

export function measureProjectSavePayloadSize(
  payload: ProjectSavePayloadLike,
): ProjectSavePayloadSizeBreakdown {
  const serializedPayload = JSON.stringify(payload);
  const titleBytes = measureTextBytes(payload.title || '');
  const descriptionBytes = measureTextBytes(payload.description || '');
  const workspaceXmlBytes = measureTextBytes(payload.workspaceXml || '');
  const thumbnailBytes = measureTextBytes(payload.thumbnail || '');
  const extensionsBytes = payload.extensions
    ? measureTextBytes(JSON.stringify(payload.extensions))
    : 0;

  return {
    limitBytes: PROJECT_SAVE_REQUEST_LIMIT_BYTES,
    totalBytes: measureTextBytes(serializedPayload),
    titleBytes,
    descriptionBytes,
    workspaceXmlBytes,
    thumbnailBytes,
    extensionsBytes,
  };
}

const resolveDominantSection = (
  breakdown: ProjectSavePayloadSizeBreakdown,
): 'thumbnail' | 'workspaceXml' | 'extensions' | 'metadata' => {
  const metadataBytes = breakdown.titleBytes + breakdown.descriptionBytes;
  const candidates: Array<{
    key: 'thumbnail' | 'workspaceXml' | 'extensions' | 'metadata';
    value: number;
  }> = [
    { key: 'thumbnail', value: breakdown.thumbnailBytes },
    { key: 'workspaceXml', value: breakdown.workspaceXmlBytes },
    { key: 'extensions', value: breakdown.extensionsBytes },
    { key: 'metadata', value: metadataBytes },
  ];

  candidates.sort((left, right) => right.value - left.value);
  return candidates[0]?.key ?? 'metadata';
};

export class ProjectPayloadTooLargeError extends Error {
  breakdown: ProjectSavePayloadSizeBreakdown;

  constructor(breakdown: ProjectSavePayloadSizeBreakdown) {
    super(
      `保存请求约 ${formatByteSize(breakdown.totalBytes)}，超过服务器允许的 ${formatByteSize(breakdown.limitBytes)} 上限。`,
    );
    this.name = 'ProjectPayloadTooLargeError';
    this.breakdown = breakdown;
  }
}

export function describeProjectPayloadTooLarge(error: ProjectPayloadTooLargeError): {
  title: string;
  content: string;
} {
  const { breakdown } = error;
  const dominantSection = resolveDominantSection(breakdown);
  let suggestion = '请优先压缩封面图片或减少项目内容后重试。';

  if (dominantSection === 'thumbnail') {
    suggestion = '当前主要是封面图片数据过大。封面保存时会转换为 Base64 文本，体积会比原文件更大，请压缩图片分辨率或文件大小后重试。';
  } else if (dominantSection === 'workspaceXml') {
    suggestion = '当前主要是工作区数据过大。请删除不需要的内容或拆分项目后重试。';
  } else if (dominantSection === 'extensions') {
    suggestion = '当前主要是扩展数据过大。请检查附加的扩展信息或减少不必要的数据后重试。';
  }

  return {
    title: '保存失败：请求内容过大',
    content: [
      `服务器单次保存请求上限为 ${formatByteSize(breakdown.limitBytes)}，当前请求约 ${formatByteSize(breakdown.totalBytes)}。`,
      `封面图片数据约 ${formatByteSize(breakdown.thumbnailBytes)}，工作区数据约 ${formatByteSize(breakdown.workspaceXmlBytes)}，扩展数据约 ${formatByteSize(breakdown.extensionsBytes)}。`,
      suggestion,
    ].join('\n'),
  };
}

export function describeThumbnailFileTooLarge(fileSize: number): {
  title: string;
  content: string;
} {
  return {
    title: '封面图片过大',
    content: [
      `当前图片文件约 ${formatByteSize(fileSize)}，原图上传上限为 ${formatByteSize(PROJECT_THUMBNAIL_FILE_LIMIT_BYTES)}。`,
      '请压缩图片后再上传。若后续保存到服务器，封面还会转换为文本一起提交，请尽量控制图片尺寸。',
    ].join('\n'),
  };
}

export function assertProjectSavePayloadWithinLimit(payload: ProjectSavePayloadLike): void {
  const breakdown = measureProjectSavePayloadSize(payload);
  if (breakdown.totalBytes <= breakdown.limitBytes) {
    return;
  }

  throw new ProjectPayloadTooLargeError(breakdown);
}